import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { Campaign, FilterStructure, ValueChain } from '../../../../graphql-planner-tool/generated';
import { ActivityValueChainActions } from './activity-value-chain.actions';
import { convertListoToOptions } from '../../shared/ra.operations';

export const activityValueChainsFeatureKey = 'activityValueChains';

export interface State extends EntityState<ValueChain> {
  loading: boolean;
  filters: any;
  totalElements: number;
  page: number;
  campaigns: Campaign[];
  loadingExport: boolean;
  filtersBE: FilterStructure[]
}

export const adapter: EntityAdapter<ValueChain> = createEntityAdapter<ValueChain>({
  selectId: (valueChain: ValueChain) => valueChain.id
});

export const initialState: State = adapter.getInitialState({
  loading: false,
  page: 0,
  totalElements: 0,
  filters: {},
  campaigns: [],
  loadingExport: false,
  filtersBE: []
});

export const reducer = createReducer(
  initialState,

  on(ActivityValueChainActions.loadValueChains, (state, action) => ({
    ...adapter.removeAll({ ...state, loading: true }),
    loadingCell: {}
  })),

  on(ActivityValueChainActions.loadValueChainsSuccess, (state, action) => ({
    ...adapter.setAll(action?.valueChains?.items as ValueChain[], state),
    loading: false,
    filtersBE: action.valueChains.filters as FilterStructure[],
    totalElements: action.valueChains.pageInfo.totalItems as number,
    pageIndex: action.valueChains.pageInfo.page as number,
    pageSize: action.valueChains.pageInfo.pageSize as number
  })),

  on(ActivityValueChainActions.loadValueChainsFailure, (state, action) => ({
    ...adapter.removeAll({ ...state, loading: false }),
    loading: false
  })),

  on(ActivityValueChainActions.loadAllValueChains, (state, action) => ({
    ...state
  })),

  on(ActivityValueChainActions.loadAllValueChainsSuccess, (state, action) => ({
    ...state,
    filters: getValueChainsFilters(action.valueChains.items as ValueChain[])
  })),

  on(ActivityValueChainActions.loadAllValueChainsFailure, (state, action) => ({
    ...state,
    filters: {}
  })),

  on(ActivityValueChainActions.loadCampaigns, (state, action) => ({
    ...adapter.removeAll({ ...state, loading: false }),
    campaigns: []
  })),

  on(ActivityValueChainActions.loadCampaignsSuccess, (state, action) => ({
    ...state,
    campaigns: action.campaigns?.items as Campaign[]
  })),

  on(ActivityValueChainActions.loadCampaignsFailure, (state, action) => ({
    ...state,
    campaigns: []
  })),

  on(ActivityValueChainActions.updateActivityValueChain, (state, action) => ({
    ...state,
    loading: true
  })),

  on(ActivityValueChainActions.updateActivityValueChainSuccess, (state, action) => ({
    ...adapter.updateOne({ id: action.update.data?.id as string, changes: action.update.data as ValueChain }, state),
    loading: false
  })),

  on(ActivityValueChainActions.updateActivityValueChainFailure, (state, action) => ({
    ...state
  })),

  on(ActivityValueChainActions.downloadActivityValueChain, (state) => ({
    ...state,
    loadingExport: true
  })),

  on(ActivityValueChainActions.downloadActivityValueChainSuccess, (state) => ({
    ...state,
    loadingExport: false
  })),

  on(ActivityValueChainActions.downloadActivityValueChainFailure, (state) => ({
    ...state,
    loadingExport: false
  }))
);

export const activityValueChainsFeature = createFeature({
  name: activityValueChainsFeatureKey,
  reducer,
  extraSelectors: ({
    selectActivityValueChainsState,
    selectEntities,
    selectIds,
    selectFilters
  }) => ({
    ...adapter.getSelectors(selectActivityValueChainsState),

    selectValueChains: createSelector(
      selectEntities,
      selectIds,
      (entities, ids) => {
        return (ids?.map(id => entities[id] ?? []) || []) as ValueChain[];
      }
    ),

    selectNames: (fieldName: string) =>
      createSelector(selectFilters, (filters) => {
        if (fieldName.includes(" ")) {
          return ['Active', 'Not Active']
        } else {
          return convertListoToOptions(filters[fieldName] || [])
        }
      }
      )
  })
});

export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
  selectValueChains,
  selectNames,
  selectTotalElements,
  selectPage,
  selectFilters,
  selectLoading,
  selectCampaigns,
  selectLoadingExport,
  selectFiltersBE
} = activityValueChainsFeature;


export function getValueChainsFilters(valueChains: ValueChain[]): Record<string, string[]> {
  const ret: Record<string, string[]> = {};

  function extract(obj: any, prefix = '') {
    for (const key in obj) {
      const value = obj[key];
      const path = prefix ? `${prefix}-${key}` : key; // concatena il path

      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        // ricorsione per oggetti annidati
        extract(value, path);
      } else {
        if (!ret[path]) ret[path] = [];
        ret[path].push(String(value));
      }
    }
  }

  for (const vc of valueChains) {
    extract(vc);
  }

  return ret;
}